import { supabase } from './supabase';
import { formatWhen } from './ctoMembers';

/**
 * People in the My AI CTO funnel, plus who has already activated.
 *
 * Waitlist status stays waitlisted / invited. Activation is a separate table.
 * Once they have a live activation they are no longer in the invite queue —
 * do not show them as Invited. They belong on Members.
 */

export const MCP_SETUP_URL = 'https://members.stephansmith.me/setup';

export const WAITLIST_INTAKE_KEYS = {
  linkedin: 'linkedin',
  stage: 'mcp_how_far_along',
  building: 'mcp_what_building',
} as const;

export type WaitlistStatus = 'waitlisted' | 'invited';

/** Funnel step on the waitlist page — activation outranks invite. */
export type WaitlistStep = 'waiting' | 'pending' | 'activated';

export interface CtoWaitlistRow {
  subscriberId: string;
  name: string | null;
  email: string;
  accessStatus: WaitlistStatus;
  waitlistedAt: string | null;
  invitedAt: string | null;
  /** Live activation timestamp; null if they never accepted terms. */
  activatedAt: string | null;
  revokedAt: string | null;
  linkedin: string | null;
  stage: string | null;
  building: string | null;
}

function str(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function customText(custom: unknown, key: string): string | null {
  if (!custom || typeof custom !== 'object' || Array.isArray(custom)) return null;
  return str((custom as Record<string, unknown>)[key]);
}

export function isLiveActivated(row: CtoWaitlistRow): boolean {
  return Boolean(row.activatedAt && !row.revokedAt);
}

export function waitlistStep(row: CtoWaitlistRow): WaitlistStep {
  if (isLiveActivated(row)) return 'activated';
  if (row.accessStatus === 'invited') return 'pending';
  return 'waiting';
}

/** Queue label — activated people are done with the invite, even if the row still says invited. */
export function queueAccessStatus(row: CtoWaitlistRow): WaitlistStatus | 'activated' {
  if (isLiveActivated(row)) return 'activated';
  return row.accessStatus;
}

export async function fetchCtoWaitlist(): Promise<CtoWaitlistRow[]> {
  const { data, error } = await supabase
    .from('subscriber_mcp_access')
    .select(
      'subscriber_id, access_status, waitlisted_at, invited_at, created_at, subscriber:subscribers(id, name, email, custom_data)',
    )
    .in('access_status', ['waitlisted', 'invited'])
    .order('waitlisted_at', { ascending: true, nullsFirst: false });

  if (error) throw new Error(error.message);

  const accessRows = data ?? [];
  const ids = accessRows.map((raw) => String((raw as Record<string, unknown>).subscriber_id));
  const activations = new Map<string, { activatedAt: string; revokedAt: string | null }>();

  if (ids.length > 0) {
    const { data: actData, error: actError } = await supabase
      .from('subscriber_mcp_activations')
      .select('subscriber_id, activated_at, revoked_at')
      .in('subscriber_id', ids);
    if (actError) throw new Error(actError.message);
    for (const raw of actData ?? []) {
      const row = raw as Record<string, unknown>;
      activations.set(String(row.subscriber_id), {
        activatedAt: String(row.activated_at),
        revokedAt: str(row.revoked_at),
      });
    }
  }

  return accessRows.map((raw) => {
    const row = raw as Record<string, unknown>;
    const subscriber = (row.subscriber ?? null) as {
      name?: unknown;
      email?: unknown;
      custom_data?: unknown;
    } | null;
    const custom = subscriber?.custom_data;
    const status = String(row.access_status) === 'invited' ? 'invited' : 'waitlisted';
    const subscriberId = String(row.subscriber_id);
    const activation = activations.get(subscriberId);

    return {
      subscriberId,
      name: str(subscriber?.name),
      email: String(subscriber?.email ?? ''),
      accessStatus: status,
      waitlistedAt: str(row.waitlisted_at) ?? str(row.created_at),
      invitedAt: str(row.invited_at),
      activatedAt: activation?.activatedAt ?? null,
      revokedAt: activation?.revokedAt ?? null,
      linkedin: customText(custom, WAITLIST_INTAKE_KEYS.linkedin),
      stage: customText(custom, WAITLIST_INTAKE_KEYS.stage),
      building: customText(custom, WAITLIST_INTAKE_KEYS.building),
    };
  });
}

/** Oldest waiters first — that is who to review this week. */
export function sortWaitlist(rows: CtoWaitlistRow[]): CtoWaitlistRow[] {
  return [...rows].sort((a, b) => {
    const aTime = Date.parse(a.waitlistedAt ?? '') || 0;
    const bTime = Date.parse(b.waitlistedAt ?? '') || 0;
    if (aTime !== bTime) return aTime - bTime;
    return (a.name ?? a.email).localeCompare(b.name ?? b.email, undefined, {
      sensitivity: 'base',
    });
  });
}

export function formatWaitDuration(iso: string | null): string {
  return formatWhen(iso);
}

/** Turn a typed LinkedIn value into an https URL, or null if they left it blank. */
export function linkedinHref(raw: string | null): string | null {
  const value = str(raw);
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  if (value.includes('linkedin.com')) {
    return `https://${value.replace(/^\/+/, '')}`;
  }
  if (/^[a-zA-Z0-9-]+$/.test(value)) {
    return `https://www.linkedin.com/in/${value}`;
  }
  return value;
}

export function firstName(name: string | null, email: string): string {
  const fromName = name?.trim().split(/\s+/)[0];
  if (fromName) return fromName;
  const local = email.split('@')[0]?.trim();
  return local || 'there';
}

export function defaultAcceptSubject(): string {
  return "You're in — get started with My AI CTO";
}

export async function saveWaitlistStatus(
  subscriberId: string,
  accessStatus: 'not_applied' | WaitlistStatus,
  existing: { waitlistedAt: string | null; invitedAt: string | null },
): Promise<void> {
  const now = new Date().toISOString();
  const { error } = await supabase.from('subscriber_mcp_access').upsert(
    {
      subscriber_id: subscriberId,
      access_status: accessStatus,
      updated_at: now,
      waitlisted_at:
        accessStatus === 'not_applied'
          ? existing.waitlistedAt
          : existing.waitlistedAt ?? now,
      invited_at: accessStatus === 'invited' ? existing.invitedAt ?? now : existing.invitedAt,
    },
    { onConflict: 'subscriber_id' },
  );
  if (error) throw error;
}

export function defaultAcceptMessage(name: string | null, email: string): string {
  return `Hi ${firstName(name, email)},

You're off the waitlist. Your account is ready — open the button below to go to setup on members.stephansmith.me and I'll walk you through how to get started.`;
}
